import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { BASE_SIZE, ARENA_SIZE } from '../../../utils/constants.js';
import { matte, colored, vary, seededRandom, noise2, jitter } from '../../Style.js';
import { SEA, foamMesh, palmParts, rockGeometry, buoyParts, ropeParts } from './style.js';

const TOP = 0.12; // Land sits just proud of the water
const STRAND = 3.5; // Depth of the back strand, against the harbour wall
const SIDE = 12; // Side arms of land start this far from the centre line
const OUTCROP_TOP = 2.95; // Turrets stand at y=3 on rocky outcrops

function smoothstep(a, b, x) {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
}

// Distance from a point to an axis-aligned rectangle (0 inside)
function rectDistance(u, z, u0, u1, z0, z1) {
  const du = Math.max(u0 - u, 0, u - u1);
  const dz = Math.max(z0 - z, 0, z - z1);
  return Math.hypot(du, dz);
}

// A team's harbour: a horseshoe of sandy land (a strand along the back wall
// and an arm down each side) around sheltered water that opens to the lagoon
// at the front. The fort, lighthouse, palms and stores stand on the land; the
// buy-zone piers run out from the strand. A painted edge on land and a line
// of team buoys on the water mark the whole base.
//
// Everything is laid out in a local frame: u runs from the back wall (0)
// towards the arena centre, z is the world z.
export class Base {
  constructor(scene, position, color) {
    this.scene = scene;
    this.position = position;
    this.color = color;
    this.meshes = [];
    this.flags = [];
    this.ownMaterials = [];
    this.rand = seededRandom(position.x > 0 ? 41 : 29);
    this.create();
  }

  add(object) {
    this.scene.add(object);
    this.meshes.push(object);
    return object;
  }

  own(material) {
    this.ownMaterials.push(material);
    return material;
  }

  create() {
    // Calculate clipped base boundaries to stay within arena
    const arenaHalf = ARENA_SIZE / 2;
    const halfBaseWidth = BASE_SIZE / 2;
    const halfBaseDepth = (BASE_SIZE * 1.15) / 2; // 15% taller in Z direction

    const westEdge = Math.max(this.position.x - halfBaseWidth, -arenaHalf);
    const eastEdge = Math.min(this.position.x + halfBaseWidth, arenaHalf);
    const northEdge = Math.max(this.position.z - halfBaseDepth, -arenaHalf);
    const southEdge = Math.min(this.position.z + halfBaseDepth, arenaHalf);

    // Which way is the back of the base (towards the harbour wall)
    this.back = this.position.x < 0 ? -1 : 1;
    this.backX = this.back < 0 ? westEdge : eastEdge;
    this.width = eastEdge - westEdge;
    this.halfDepth = (southEdge - northEdge) / 2;
    this.centerZ = (northEdge + southEdge) / 2;

    this.createLand();
    this.createShoreFoam();
    this.createProps();
  }

  // Local (u, z) to world x
  wx(u) {
    return this.backX - this.back * u;
  }

  // Distance from the horseshoe of land (0 on land)
  landDistance(u, z) {
    const hz = this.halfDepth;
    const lz = z - this.centerZ;
    return Math.min(
      rectDistance(u, lz, -2, STRAND, -hz, hz),
      rectDistance(u, lz, -2, this.width, SIDE, hz),
      rectDistance(u, lz, -2, this.width, -hz, -SIDE)
    );
  }

  // The land as a heightfield: flat sand on the horseshoe, shelving under the water
  createLand() {
    const margin = 4;
    const u0 = -0.5;
    const u1 = this.width + margin;
    const z0 = this.centerZ - this.halfDepth - margin;
    const z1 = this.centerZ + this.halfDepth + margin;
    const geometry = new THREE.PlaneGeometry(u1 - u0, z1 - z0, Math.ceil((u1 - u0) / 0.8), Math.ceil((z1 - z0) / 0.8));
    geometry.rotateX(-Math.PI / 2);
    geometry.translate((u0 + u1) / 2, 0, (z0 + z1) / 2);
    const pos = geometry.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      const u = pos.getX(i);
      const z = pos.getZ(i);
      const d = this.landDistance(u, z);
      // A wavering shoreline
      const wobble = d > 0 ? noise2(u * 0.3 + this.back * 7, z * 0.3) * 0.6 : 0;
      pos.setY(i, TOP - smoothstep(0.3, 2.6, d + wobble) * 0.9);
      pos.setX(i, this.wx(u));
    }
    // Mirroring the x axis for the far base flips the winding; flip it back
    if (this.back > 0) {
      const index = geometry.index.array;
      for (let i = 0; i < index.length; i += 3) {
        const t = index[i + 1];
        index[i + 1] = index[i + 2];
        index[i + 2] = t;
      }
    }

    const flat = geometry.toNonIndexed();
    flat.computeVertexNormals();
    const fpos = flat.attributes.position;
    const colors = new Float32Array(fpos.count * 3);
    const c = new THREE.Color();
    const grass = new THREE.Color(SEA.grass);
    const rand = this.rand;
    for (let i = 0; i < fpos.count; i += 3) {
      const y = (fpos.getY(i) + fpos.getY(i + 1) + fpos.getY(i + 2)) / 3;
      const x = (fpos.getX(i) + fpos.getX(i + 1) + fpos.getX(i + 2)) / 3;
      const z = (fpos.getZ(i) + fpos.getZ(i + 1) + fpos.getZ(i + 2)) / 3;
      const u = (this.backX - x) * -this.back;
      if (y > TOP - 0.02) {
        c.set(SEA.sand).lerp(new THREE.Color(SEA.sandDark), noise2(x * 0.3, z * 0.3) * 0.5 + 0.5);
        // Grass grows along the back, well away from the water
        const inland = Math.min(u, Math.abs(Math.abs(z - this.centerZ) - this.halfDepth));
        if (u < 2.2 && noise2(x * 0.2, z * 0.2) > -0.2 && inland < 2.5) c.lerp(grass, 0.75);
      } else {
        c.set(SEA.sandWet);
      }
      c.offsetHSL(0, 0, (rand() - 0.5) * 0.03);
      for (let k = 0; k < 3; k++) colors.set([c.r, c.g, c.b], (i + k) * 3);
    }
    flat.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    flat.deleteAttribute('uv');
    const land = new THREE.Mesh(flat, matte(0xffffff, { vertexColors: true, roughness: 1 }));
    land.receiveShadow = true;
    this.add(land);
  }

  // Surf along every waterline of the horseshoe, inside the harbour and out
  createShoreFoam() {
    const w = this.width;
    const hz = this.halfDepth;
    const shore = 0.8;
    const edges = [
      // Harbour side of the strand
      [[STRAND, -SIDE], [STRAND, SIDE], [1, 0]],
      // Harbour sides of the arms
      [[STRAND, SIDE], [w, SIDE], [0, -1]],
      [[STRAND, -SIDE], [w, -SIDE], [0, 1]],
      // Lagoon sides of the arms
      [[0, hz], [w, hz], [0, 1]],
      [[0, -hz], [w, -hz], [0, -1]],
      // Tips of the arms
      [[w, SIDE], [w, hz], [1, 0]],
      [[w, -hz], [w, -SIDE], [1, 0]]
    ];
    const positions = [];
    const edge = [];
    const indices = [];
    const spread = 1.8;
    for (const [[au, az], [bu, bz], [nu, nz]] of edges) {
      const steps = Math.max(1, Math.round(Math.hypot(bu - au, bz - az) / 1.5));
      const start = positions.length / 3;
      for (let s = 0; s <= steps; s++) {
        const t = s / steps;
        const u = au + (bu - au) * t + nu * shore;
        const z = az + (bz - az) * t + nz * shore + this.centerZ;
        positions.push(this.wx(u), 0, z, this.wx(u + nu * spread), 0, z + nz * spread);
        edge.push(0, 1);
        if (s < steps) {
          const k = start + s * 2;
          indices.push(k, k + 2, k + 1, k + 1, k + 2, k + 3);
        }
      }
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('aEdge', new THREE.Float32BufferAttribute(edge, 1));
    geometry.setIndex(indices);
    this.add(foamMesh(geometry, 0.05));
  }

  createProps() {
    const rand = this.rand;
    const team = new THREE.Color(this.color);
    const back = this.back;
    const w = this.width;
    const hz = this.halfDepth;
    const cz = this.centerZ;
    const parts = [];
    const at = (u, z) => ({ x: this.wx(u), z: cz + z });

    // Team-painted edging along the outer edge of each arm, with team-capped posts
    for (const side of [-1, 1]) {
      const z = side * (hz - 0.5);
      const mid = at(w / 2, z);
      parts.push(colored(new THREE.BoxGeometry(w, 0.04, 0.6), team, { x: mid.x, y: TOP + 0.02, z: mid.z }));
      // And across the tip of the arm
      const tip = at(w - 0.35, side * (SIDE + hz) / 2);
      parts.push(colored(new THREE.BoxGeometry(0.6, 0.04, hz - SIDE), team, { x: tip.x, y: TOP + 0.02, z: tip.z }));
      for (let u = 1.5; u < w; u += 3) {
        const p = at(u, side * (hz - 0.1));
        parts.push(colored(new THREE.CylinderGeometry(0.1, 0.12, 1.1, 6), SEA.woodDark, { x: p.x, y: TOP + 0.55, z: p.z }));
        parts.push(colored(new THREE.CylinderGeometry(0.15, 0.15, 0.25, 6), team, { x: p.x, y: TOP + 1.15, z: p.z }));
      }
    }

    // Team buoys on a rope round the seaward edge and across the harbour mouth
    const off = 2.6;
    const loop = [
      [0.5, -(hz + off)], [w + off, -(hz + off)], [w + off, hz + off], [0.5, hz + off]
    ];
    let prev = null;
    for (let s = 0; s < loop.length - 1; s++) {
      const [au, az] = loop[s];
      const [bu, bz] = loop[s + 1];
      const count = Math.max(1, Math.round(Math.hypot(bu - au, bz - az) / 3.5));
      for (let i = s === 0 ? 0 : 1; i <= count; i++) {
        const t = i / count;
        const p = at(au + (bu - au) * t, az + (bz - az) * t);
        // Skip buoys that would sit against the harbour wall
        if (Math.abs(p.x) > ARENA_SIZE / 2 - 1.5 || Math.abs(p.z) > ARENA_SIZE / 2 - 1.5) continue;
        parts.push(...buoyParts(i % 2 ? team : SEA.white, { x: p.x, y: -0.05, z: p.z, height: 1.0 }));
        const top = new THREE.Vector3(p.x, 0.3, p.z);
        if (prev) parts.push(...ropeParts(prev, top, { sag: 0.2, radius: 0.05, segments: 3 }));
        prev = top;
      }
    }

    // Rocky outcrops carrying the base's turrets
    for (const [u, z] of [[2, 15], [2, -15], [w + 0.5, 15], [w + 0.5, -15]]) {
      const p = at(u, z);
      const outcrop = jitter(new THREE.CylinderGeometry(1.9, 2.6, OUTCROP_TOP + 0.6, 7, 3), 0.35, rand);
      // Flatten the jittered top back to the turret's footing
      const op = outcrop.attributes.position;
      for (let i = 0; i < op.count; i++) {
        if (op.getY(i) > (OUTCROP_TOP + 0.6) / 2 - 0.4) op.setY(i, (OUTCROP_TOP + 0.6) / 2);
      }
      outcrop.computeVertexNormals();
      parts.push(colored(outcrop, vary(SEA.rock, rand, 0.1), { x: p.x, y: (OUTCROP_TOP + 0.6) / 2 - 0.6, z: p.z }));
      parts.push(colored(new THREE.CylinderGeometry(1.95, 1.95, 0.08, 7), SEA.grass, { x: p.x, y: OUTCROP_TOP, z: p.z }));
      for (let i = 0; i < 3; i++) {
        const a = rand() * Math.PI * 2;
        parts.push(rockGeometry(rand, 0.6 + rand() * 0.4, vary(SEA.rockDark, rand, 0.1), { x: p.x + Math.cos(a) * 2.6, y: 0, z: p.z + Math.sin(a) * 2.6, squash: 0.6 }));
      }
    }

    // Palms along the arms
    for (const side of [-1, 1]) {
      for (const [u, dz] of [[6.5, 0.2], [13.5, -0.3], [19.5, 0.1]]) {
        const p = at(u + (side > 0 ? 1.5 : 0), side * (SIDE + 1.2 + dz));
        parts.push(...palmParts(rand, { x: p.x, y: TOP - 0.1, z: p.z, ry: rand() * Math.PI * 2, height: 5 + rand() * 2, lean: 0.3 + rand() * 0.25 }));
      }
    }

    // Stone bastion in one back corner, with a short curtain wall and a cannon
    const bastion = at(1.8, 10.4);
    parts.push(colored(new THREE.CylinderGeometry(1.6, 1.8, 3.4, 10), vary(SEA.stone, rand, 0.06), { x: bastion.x, y: 1.7, z: bastion.z }));
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      parts.push(colored(new THREE.BoxGeometry(0.5, 0.5, 0.45), SEA.stone, { x: bastion.x + Math.cos(a) * 1.45, y: 3.65, z: bastion.z + Math.sin(a) * 1.45, ry: -a }));
    }
    parts.push(colored(new THREE.CylinderGeometry(1.85, 1.85, 0.35, 10), SEA.seaweed, { x: bastion.x, y: 0.18, z: bastion.z }));
    parts.push(colored(new THREE.CylinderGeometry(0.15, 0.2, 1.5, 8), SEA.iron, { x: bastion.x - back * 0.9, y: 4.1, z: bastion.z, rz: Math.PI / 2 - 0.15 }));
    const wallFrom = at(0.6, 12);
    const wallTo = at(0.6, hz);
    parts.push(colored(new THREE.BoxGeometry(1.0, 2.2, wallTo.z - wallFrom.z), vary(SEA.stoneDark, rand, 0.05), { x: wallFrom.x, y: 1.1, z: (wallFrom.z + wallTo.z) / 2 }));
    for (let z = wallFrom.z + 0.4; z < wallTo.z; z += 1.0) {
      parts.push(colored(new THREE.BoxGeometry(1.0, 0.45, 0.5), SEA.stone, { x: wallFrom.x, y: 2.4, z }));
    }

    // Lighthouse in the other back corner, banded in the team colour
    const light = at(1.8, -10.6);
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      parts.push(rockGeometry(rand, 0.85, vary(SEA.rock, rand, 0.1), { x: light.x + Math.cos(a) * 1.2, y: 0.3, z: light.z + Math.sin(a) * 1.2, squash: 0.6 }));
    }
    const bands = 5;
    const towerHeight = 8;
    for (let i = 0; i < bands; i++) {
      const t0 = i / bands;
      const t1 = (i + 1) / bands;
      parts.push(colored(new THREE.CylinderGeometry(1.2 - t1 * 0.45, 1.2 - t0 * 0.45, towerHeight / bands, 10), i % 2 ? team : SEA.white, {
        x: light.x, y: 0.5 + (t0 + t1) / 2 * towerHeight, z: light.z
      }));
    }
    const gallery = 0.5 + towerHeight;
    parts.push(colored(new THREE.CylinderGeometry(1.1, 0.95, 0.25, 10), SEA.iron, { x: light.x, y: gallery + 0.12, z: light.z }));
    parts.push(colored(new THREE.ConeGeometry(0.85, 0.9, 10), team, { x: light.x, y: gallery + 1.75, z: light.z }));
    parts.push(colored(new THREE.CylinderGeometry(0.04, 0.04, 1.8, 4), SEA.iron, { x: light.x, y: gallery + 2.9, z: light.z }));

    // Stores on the strand and arms: crates, barrels and fishing nets drying on racks
    const crates = [at(0.9, 0), at(1.4, 1.1), at(w - 3, 13.4), at(w - 3.6, -13.3)];
    crates.forEach((p, i) => {
      parts.push(colored(new THREE.BoxGeometry(0.9, 0.8, 0.9), vary(SEA.woodLight, rand, 0.1), { x: p.x, y: TOP + 0.4, z: p.z, ry: rand() * 0.6 }));
      if (i % 2 === 0) parts.push(colored(new THREE.BoxGeometry(0.6, 0.55, 0.6), vary(SEA.woodLight, rand, 0.1), { x: p.x, y: TOP + 1.07, z: p.z, ry: rand() }));
    });
    for (const p of [at(0.9, -1.3), at(w - 2, 13.3), at(9.5, -13.4)]) {
      for (let i = 0; i < 2; i++) {
        parts.push(colored(new THREE.CylinderGeometry(0.3, 0.3, 0.8, 8), vary(SEA.wood, rand, 0.1), { x: p.x + i * 0.65, y: TOP + 0.4, z: p.z }));
        parts.push(colored(new THREE.CylinderGeometry(0.32, 0.32, 0.06, 8), SEA.iron, { x: p.x + i * 0.65, y: TOP + 0.6, z: p.z }));
      }
    }
    for (const side of [-1, 1]) {
      const a = at(9.8, side * 13.4);
      const b = at(12.6, side * 13.4);
      for (const p of [a, b]) parts.push(colored(new THREE.CylinderGeometry(0.07, 0.09, 1.9, 5), SEA.woodDark, { x: p.x, y: TOP + 0.95, z: p.z }));
      const span = Math.abs(b.x - a.x);
      const mx = (a.x + b.x) / 2;
      for (let i = 0; i <= 6; i++) {
        parts.push(colored(new THREE.BoxGeometry(0.025, 1.4, 0.025), SEA.rope, { x: a.x + (b.x - a.x) * (i / 6), y: TOP + 1.1, z: a.z }));
      }
      for (let k = 0; k < 4; k++) {
        parts.push(colored(new THREE.BoxGeometry(span, 0.025, 0.025), SEA.rope, { x: mx, y: TOP + 0.5 + k * 0.4, z: a.z }));
      }
    }

    // Flagpoles at the tips of the arms
    for (const side of [-1, 1]) {
      const p = at(w - 1, side * (SIDE + 1.6));
      parts.push(colored(new THREE.CylinderGeometry(0.09, 0.12, 5.5, 6), SEA.woodDark, { x: p.x, y: 2.75, z: p.z }));
      parts.push(colored(new THREE.SphereGeometry(0.16, 6, 4), 0xd9b25a, { x: p.x, y: 5.55, z: p.z }));
      this.createFlag(p.x, 4.6, p.z, 2.0);
    }
    this.createFlag(light.x, gallery + 3.4, light.z, 1.5);

    const props = new THREE.Mesh(mergeGeometries(parts), matte(0xffffff, { vertexColors: true }));
    props.castShadow = true;
    props.receiveShadow = true;
    this.add(props);

    // The lamp glows in the team colour
    this.lamp = new THREE.Mesh(new THREE.CylinderGeometry(0.62, 0.62, 1.0, 10), this.own(new THREE.MeshBasicMaterial({
      color: team.clone().lerp(new THREE.Color(0xffffff), 0.35).multiplyScalar(1.3), toneMapped: false
    })));
    this.lamp.position.set(light.x, gallery + 0.75, light.z);
    this.add(this.lamp);
  }

  // A cloth flag whose vertices ripple in the breeze each frame.
  createFlag(x, y, z, width) {
    const geometry = new THREE.PlaneGeometry(width, width * 0.6, 8, 3);
    geometry.translate(width / 2, 0, 0);
    const material = this.own(new THREE.MeshStandardMaterial({ color: this.color, side: THREE.DoubleSide, roughness: 0.9, flatShading: true }));
    const flag = new THREE.Mesh(geometry, material);
    flag.position.set(x, y, z);
    // The trade wind blows the flags out to sea
    flag.rotation.y = this.position.x < 0 ? Math.PI : 0;
    flag.castShadow = true;
    flag.userData.phase = x * 0.3 + z * 0.2;
    this.add(flag);
    this.flags.push(flag);
  }

  update(elapsed) {
    for (const flag of this.flags) {
      const pos = flag.geometry.attributes.position;
      for (let i = 0; i < pos.count; i++) {
        const bx = pos.getX(i);
        pos.setZ(i, Math.sin(elapsed * 4 + bx * 2 + flag.userData.phase) * 0.15 * bx);
      }
      pos.needsUpdate = true;
      flag.geometry.computeVertexNormals();
    }
    if (this.lamp) this.lamp.scale.setScalar(1 + Math.sin(elapsed * 3) * 0.04);
  }

  remove() {
    const geometries = new Set();
    this.meshes.forEach(mesh => {
      this.scene.remove(mesh);
      mesh.traverse?.(child => {
        if (child.isMesh) geometries.add(child.geometry);
      });
    });
    geometries.forEach(geometry => geometry.dispose());
    this.ownMaterials.forEach(material => material.dispose());
    this.ownMaterials = [];
    this.meshes = [];
    this.flags = [];
  }
}
